import * as Notifications from '@openfin/notifications'
import type { Dispatch } from 'react'
import { Activity } from '../../store/activity/types'
import { setSecuritySearch } from '../../store/benchmarkData/actions'
import { setQueryFilter } from '../../store/securities/actions'
import type { StoreAction } from '../../store/types'

// Identity name of the main view (Pricing Panel grid).
export const MAIN_WINDOW_NAME = 'Pricing Panel'

// Identity name of the frame that hosts the Pricing Panel view
export const MAIN_FRAME_NAME = 'Bonds.com'

// True when running inside an OpenFin platform view (not a plain browser tab).
export const isOpenFinView = () => typeof fin !== 'undefined' && fin.me.isView

// True when running in the OpenFin runtime at all (any window or view)
export const isOpenFinRuntime = () => typeof fin !== 'undefined'

/*
    Detects that the start time was crossed between two polls rather than that
    it is the start time right now. A minimized window is a hidden page, so the
    browser throttles the poll to roughly once a minute - any fixed match window
    would be missed. Ranges are checked up front because setHours rolls an
    out-of-range value into another day instead of failing.
*/
export const autoStartElapsed = (startTime: string, since: Date, now: Date) => {
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(startTime)
  if (!match) return false
  const target = new Date(now)
  target.setHours(Number(match[1]), Number(match[2]), 0, 0)
  return since < target && target <= now
}

// True only in the main view, which is where the Workspace Notifications SDK
// is registered. Popouts run the same epics but never call Notifications.register().
export const isMainOpenFinWindow = () =>
  isOpenFinRuntime() && fin.me.identity.name === MAIN_WINDOW_NAME

// Surface the main grid's Pricing Panel tab so a benchmark search isn't invisible when it's a background tab/window.
export const focusMainGrid = async () => {
  if (typeof fin === 'undefined') return
  try {
    const view = fin.View.wrapSync({
      uuid: fin.me.identity.uuid,
      name: MAIN_WINDOW_NAME
    })
    const win = await view.getCurrentWindow()
    if ((await win.getState()) === 'minimized') await win.restore()
    await win.setAsForeground()
    await view.focus()
  } catch (err) {
    console.warn('Failed to focus main grid view:', err)
  }
}

// task ids shared by createOpenFinAlert's buttons and useOpenFin's handler.
export const NotificationTasks = {
  ViewDetails: 'view-details',
  PopoutDepth: 'popout-depth',
  AcceptOrder: 'accept-order',
  RejectOrder: 'reject-order'
} as const
export type NotificationTask =
  (typeof NotificationTasks)[keyof typeof NotificationTasks]

/* Topic + payload contract agreed with the external OpenFin app. They publish
the globally-selected instrument as a flat { TICKER?, ISIN, CUSIP? } JSON
message on this topic. */
export const EXTERNAL_ISIN_SEARCH_TOPIC = 'INTEROP/GLB/ALL/InstrumentUpdate'

const MAX_ISIN_LENGTH = 24
const EXTERNAL_SEARCH_DEBOUNCE_MS = 300

/* Any app in the runtime can publish on the topic, so the contract above is a
claim rather than a guarantee — narrow the payload before trusting it. */
export const validateMessageIsin = (message: unknown) => {
  if (!message || typeof message !== 'object' || !('ISIN' in message)) return
  const { ISIN } = message
  if (typeof ISIN !== 'string') return
  const isin = ISIN.trim()
  if (!isin || isin.length > MAX_ISIN_LENGTH) return
  return isin
}

/* Subscribe to instrument selections from the external OpenFin app and run the
same search the benchmark buttons run. Returns an unsubscribe function.
The vendor's uuid rotates, so the source can't be pinned to their app; the
payload is validated and debounced instead. */
export const subscribeExternalIsinSearch = (
  dispatch: Dispatch<StoreAction>
) => {
  let debounceTimer: ReturnType<typeof setTimeout> | undefined
  const listener = (message: unknown) => {
    const isin = validateMessageIsin(message)
    if (!isin) return
    clearTimeout(debounceTimer)
    debounceTimer = setTimeout(() => {
      dispatch(setSecuritySearch(0, isin))
      dispatch(setQueryFilter(0, isin))
    }, EXTERNAL_SEARCH_DEBOUNCE_MS)
  }
  const source = { uuid: '*' }
  void fin.InterApplicationBus.subscribe(
    source,
    EXTERNAL_ISIN_SEARCH_TOPIC,
    listener
  )
  return () => {
    clearTimeout(debounceTimer)
    void fin.InterApplicationBus.unsubscribe(
      source,
      EXTERNAL_ISIN_SEARCH_TOPIC,
      listener
    )
  }
}

/*
  Bridges the few display-only values the OpenFin WindowFrame's titlebar needs
  (it has its own unauthenticated store with no WS/Hub) from the authed main
  view via localStorage — deliberately NOT redux-state-sync: whitelisting
  market.marketClosed would fire the sign-out epics in every window, and the
  auth action would cause a RefreshToken storm. localStorage touches no epics.
  The frame re-reads on the `storage` event it already listens to.
*/
export const FRAME_DISPLAY_KEY = 'frame-display'

export interface FrameDisplay {
  isAdmin: boolean
  marketClosed: boolean
  // resolved name of the user an admin is acting on behalf of ('' = none); the
  // frame can't resolve it from the synced id since it has no users list
  actingOnBehalfOf: string
}

const DEFAULT: FrameDisplay = {
  isAdmin: false,
  marketClosed: false,
  actingOnBehalfOf: ''
}

export const writeFrameDisplay = (info: FrameDisplay) => {
  try {
    window.localStorage.setItem(FRAME_DISPLAY_KEY, JSON.stringify(info))
  } catch (err) {
    console.warn('Failed to write frame display', err)
  }
}

export const readFrameDisplay = (): FrameDisplay => {
  try {
    const raw = window.localStorage.getItem(FRAME_DISPLAY_KEY)
    return raw ? { ...DEFAULT, ...JSON.parse(raw) } : DEFAULT
  } catch {
    return DEFAULT
  }
}

interface WindowConfig {
  windowHeight: number
  windowWidth: number
  isAdminWindow?: boolean
  showTaskbarIcon?: boolean
  includeInSnapshots?: boolean
  hasHeaders?: boolean
  title?: string
  // Dock views as tabs instead of new windows. 'firstSibling': the first opens
  // its own window, the rest dock into it.
  dockTarget?: 'mainWindow' | 'firstSibling'
}

export const openFinConfig: Record<string, WindowConfig> = {
  ActivityLog: {
    windowHeight: 500,
    windowWidth: 1000,
    title: 'Activity Log'
  },
  Aggressor: {
    windowHeight: 458,
    windowWidth: 415,
    hasHeaders: false,
    includeInSnapshots: false,
    title: 'Aggressor'
  },
  AlertManager: {
    windowHeight: 710,
    windowWidth: 440,
    title: 'Alert Manager'
  },
  Benchmarks: {
    windowHeight: 85,
    windowWidth: 980,
    title: 'Benchmarks'
  },
  Depth: {
    windowHeight: 365,
    windowWidth: 610,
    title: 'Depth',
    dockTarget: 'firstSibling'
  },
  LastLook: {
    windowHeight: 464,
    windowWidth: 415,
    hasHeaders: false,
    includeInSnapshots: false,
    title: 'Last Look'
  },
  ListTrading: {
    windowHeight: 500,
    windowWidth: 1500,
    title: 'List Trading'
  },
  PassiveOrders: {
    windowHeight: 500,
    windowWidth: 1030,
    title: 'Passive Orders'
  },
  RFQs: {
    windowHeight: 430,
    windowWidth: 400,
    hasHeaders: false,
    includeInSnapshots: false,
    title: 'RFQ'
  },
  RFQManager: {
    windowHeight: 500,
    windowWidth: 1000,
    title: 'RFQ Manager'
  },
  Settings: {
    windowHeight: 550,
    windowWidth: 440,
    hasHeaders: false,
    includeInSnapshots: false,
    title: 'Settings'
  },
  TradeBlotter: {
    windowHeight: 800,
    windowWidth: 1000,
    title: 'Trade Blotter'
  },
  TradeConfirm: {
    windowHeight: 500,
    windowWidth: 530,
    hasHeaders: false,
    includeInSnapshots: false,
    title: 'Trade Confirm'
  },
  Watchlists: {
    windowHeight: 500,
    windowWidth: 985,
    title: 'Watchlist',
    dockTarget: 'mainWindow'
  },
  // ADMIN WINDOWS
  ActivityTicker: {
    windowHeight: 500,
    windowWidth: 1090,
    isAdminWindow: true,
    title: 'Activity Ticker'
  },
  AggressAttempts: {
    windowHeight: 500,
    windowWidth: 1030,
    isAdminWindow: true,
    title: 'Aggress Attempts'
  },
  BBMISIN: {
    windowHeight: 407,
    windowWidth: 500,
    isAdminWindow: true,
    title: 'BBMISIN'
  },
  ExternalLiquidityStats: {
    windowHeight: 500,
    windowWidth: 1030,
    isAdminWindow: true,
    title: 'External Liquidity Stats'
  },
  ManagementReports: {
    windowHeight: 500,
    windowWidth: 415,
    isAdminWindow: true,
    title: 'Management Reports'
  },
  QueueStats: {
    windowHeight: 500,
    windowWidth: 735,
    isAdminWindow: true,
    title: 'Queue Stats'
  },
  RestrictedTrading: {
    windowHeight: 500,
    windowWidth: 700,
    isAdminWindow: true,
    title: 'Restricted Trading'
  },
  SystemParameters: {
    windowHeight: 500,
    windowWidth: 1050,
    isAdminWindow: true,
    title: 'System Parameters'
  },
  Tiers: {
    windowHeight: 500,
    windowWidth: 750,
    isAdminWindow: true,
    title: 'Tiers'
  },
  UserDashboard: {
    windowHeight: 500,
    windowWidth: 900,
    isAdminWindow: true,
    title: 'User Dashboard'
  },
  UserData: {
    windowHeight: 500,
    windowWidth: 1030,
    isAdminWindow: true,
    title: 'User Data'
  },
  UserEditor: {
    windowHeight: 700,
    windowWidth: 800,
    isAdminWindow: true,
    title: 'User Editor'
  },
  WatchlistManager: {
    windowHeight: 500,
    windowWidth: 900,
    isAdminWindow: true,
    title: 'Watchlist Manager'
  }
}

export const copyAppStyles = (window: Window) => {
  const stylesheets = Array.from(document.styleSheets)
  stylesheets.forEach((stylesheet) => {
    let rules: CSSRuleList | null = null
    try {
      rules = stylesheet.cssRules
    } catch {
      rules = null
    }
    if (rules && rules.length > 0) {
      const styleElement = window.document.createElement('style')
      Array.from(rules).forEach((rule) => {
        styleElement.appendChild(window.document.createTextNode(rule.cssText))
      })
      window.document.head.appendChild(styleElement)
    } else if (stylesheet.href) {
      const linkElement = window.document.createElement('link')
      linkElement.rel = 'stylesheet'
      linkElement.href = stylesheet.href
      window.document.head.appendChild(linkElement)
    }
  })

  // Copy the theme class from the body element
  const originalBody = document.body
  const newBody = window.document.body

  newBody.className = originalBody.className
}

export const createOpenFinAlert = (alerts: Activity[]) => {
  alerts.forEach((alert) => {
    const getIndicatorColor = () => {
      if (['pendingTrade', 'trade', 'tradeAttempt'].includes(alert.type)) {
        return alert.cardColor === 'blue'
          ? Notifications.IndicatorColor.BLUE
          : Notifications.IndicatorColor.RED
      } else return Notifications.IndicatorColor.YELLOW
    }
    const getToastType = () => {
      if (alert.type === 'alert' && alert.alertType === 'newOrder') {
        // only shows in notification center
        return 'none'
      }
      if (alert.type === 'pendingTrade') {
        return 'sticky'
      }
      return 'transient'
    }
    const getButtons = () => {
      const buttons = []
      if (alert.type === 'trade') {
        buttons.push({
          cta: true,
          title: 'Details',
          onClick: {
            task: NotificationTasks.ViewDetails,
            id: alert.id
          }
        })
      }
      if (!alert.isPending && !alert.hasAggressorActions) {
        buttons.push({
          cta: true,
          title: 'Market',
          onClick: {
            task: NotificationTasks.PopoutDepth,
            id: alert.securityId
          }
        })
      }
      if (alert.isPending && alert.hasLastLookActions) {
        buttons.push(
          {
            cta: true,
            title: 'Reject',
            onClick: {
              task: NotificationTasks.RejectOrder,
              id: alert.id
            }
          },
          {
            cta: true,
            title: 'Accept',
            onClick: {
              task: NotificationTasks.AcceptOrder,
              id: alert.id
            }
          }
        )
      }

      return buttons
    }
    const showNotification = async () => {
      await Notifications.create({
        platform: fin.me.identity.uuid,
        indicator: {
          color: getIndicatorColor(),
          text: alert.formattedTitle?.toString() ?? alert.title
        },
        title: alert.type === 'trade' ? alert.description : '',
        body:
          alert.type === 'trade' ? alert.badgeInfo ?? '' : alert.description,
        category: alert.type,
        toast: getToastType(),
        id: alert.id,
        date: alert.date,
        expires: alert.expiration,
        allowReminder: false,
        buttons: getButtons()
      })
    }
    showNotification().catch((err) =>
      console.error('Failed to create notification', err)
    )
  })
}
